<template>
  <div class="portal-shell">
    <!-- ═══ HEADER ═══ -->
    <header class="portal-header">
      <div class="gold-accent-line"></div>

      <!-- Logo area -->
      <div class="header-logo-area">
        <a href="#" class="logo-link">
          <img
            src="https://sistemas.upb.edu/img/logo-upbnet-01.png"
            alt="UPBnet"
            class="logo-img"
          />
        </a>
      </div>

      <!-- Navbar -->
      <nav class="header-nav">
        <button
          class="sidebar-toggle"
          @click="sidebarOpen = !sidebarOpen"
          aria-label="Alternar navegación"
        >
          <i class="fas fa-bars"></i>
        </button>

        <div class="header-actions">
          <!-- Mode switch -->
          <router-link to="/dashboard" class="mode-switch-btn">
            <i class="fas fa-exchange-alt"></i>
            <span class="mode-label">Vista clásica</span>
          </router-link>

          <!-- Notifications -->
          <button class="hdr-icon-btn" aria-label="Notificaciones">
            <i class="fas fa-bell"></i>
            <span class="notif-dot"></span>
          </button>

          <!-- User dropdown -->
          <div class="usr-dd-wrap" ref="userDropdown">
            <button class="usr-dd-trigger" @click="userMenuOpen = !userMenuOpen">
              <div class="usr-avatar-sm">EP</div>
              <div class="usr-info-sm">
                <p class="usr-name-sm">Estudiante</p>
                <p class="usr-role-sm">Estudiante Pregrado</p>
              </div>
              <i
                class="fas fa-chevron-down usr-dd-chevron"
                :class="{ rotated: userMenuOpen }"
              ></i>
            </button>

            <div v-if="userMenuOpen" class="usr-dd-menu">
              <div class="dd-user-hdr">
                <div class="usr-avatar-lg">EP</div>
                <div>
                  <p class="dd-user-name">Estudiante</p>
                  <p class="dd-user-role">Estudiante Pregrado</p>
                </div>
              </div>
              <div class="dd-items">
                <a href="#" class="dd-item">
                  <i class="fas fa-user dd-item-icon"></i> Mi Perfil
                </a>
                <a href="#" class="dd-item">
                  <i class="fas fa-cog dd-item-icon"></i> Cambiar Contraseña
                </a>
              </div>
              <div class="dd-footer">
                <a href="#" class="dd-logout">
                  <i class="fas fa-sign-out-alt"></i> Cerrar Sesión
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>

    <!-- Mobile overlay -->
    <div
      v-if="sidebarOpen"
      class="sb-overlay lg-hide"
      @click="sidebarOpen = false"
    ></div>

    <!-- ═══ SIDEBAR ═══ -->
    <aside class="sb" :class="{ open: sidebarOpen }">
      <!-- User panel -->
      <div class="sb-user">
        <div class="sb-avatar">EP</div>
        <div>
          <p class="sb-uname">Estudiante</p>
          <div class="sb-online">
            <span class="sb-online-dot"></span>
            <span>En línea</span>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="sb-nav">
        <ul>
          <li>
            <a
              href="http://www.upbvirtual.net/upbvirtual/"
              target="_blank"
              rel="noopener noreferrer"
              class="sb-link"
            >
              <i class="fas fa-globe sb-icon"></i>
              <span>UPB Virtual</span>
            </a>
          </li>
          <li>
            <a href="#" class="sb-link sb-active">
              <i class="fas fa-home sb-icon"></i>
              <span>Inicio</span>
            </a>
          </li>
          <li>
            <a href="#" class="sb-link sb-link-between">
              <span class="sb-link-left">
                <i class="fas fa-book sb-icon"></i>
                <span>Biblioteca</span>
              </span>
              <span class="sb-badge sb-badge-green">EBSCO</span>
            </a>
          </li>

          <!-- Collapsible groups -->
          <li v-for="group in navGroups" :key="group.key">
            <button class="sb-link" @click="toggleMenu(group.key)">
              <i :class="group.iconClass + ' sb-icon'"></i>
              <span class="sb-link-grow">{{ group.label }}</span>
              <i
                class="fas fa-chevron-down sb-chev"
                :class="{ 'sb-chev-open': openMenus[group.key] }"
              ></i>
            </button>
            <ul v-if="openMenus[group.key]" class="sb-sub">
              <li v-for="item in group.items" :key="item.label">
                <a
                  :href="item.href || '#'"
                  :target="item.external ? '_blank' : undefined"
                  :rel="item.external ? 'noopener noreferrer' : undefined"
                  class="sb-sub-link"
                >
                  <span class="sb-sub-left">
                    <i class="fas fa-chevron-right sb-sub-arrow"></i>
                    {{ item.label }}
                  </span>
                  <span
                    v-if="item.badge"
                    class="sb-badge"
                    :class="item.badge.cls"
                  >{{ item.badge.text }}</span>
                </a>
              </li>
            </ul>
          </li>

          <li class="sb-divider"></li>

          <li>
            <a href="#" class="sb-link">
              <i class="fas fa-envelope sb-icon"></i>
              <span>Contacto</span>
            </a>
          </li>
          <li>
            <a href="#" class="sb-link">
              <i class="fas fa-comment sb-icon"></i>
              <span>Sugerencias y Reclamos</span>
            </a>
          </li>
          <li>
            <a href="#" class="sb-link sb-link-logout">
              <i class="fas fa-sign-out-alt"></i>
              <span>Salir</span>
            </a>
          </li>
        </ul>
      </nav>

      <div class="sb-footer">
        UPBnet &copy; {{ currentYear }} &middot; Universidad Privada Boliviana
      </div>
    </aside>

    <!-- ═══ MAIN CONTENT AREA ═══ -->
    <div class="main-area" :class="{ shifted: sidebarOpen }">
      <!-- Content header / breadcrumb -->
      <section class="ct-header">
        <div class="ct-header-inner">
          <div>
            <div class="ct-title-row">
              <div class="ct-title-bar"></div>
              <h1 class="ct-title">Estudiante Pregrado</h1>
            </div>
            <p class="ct-subtitle">Panel de Control</p>
          </div>
          <ol class="ct-crumbs">
            <li>Inicio</li>
          </ol>
        </div>
      </section>

      <section class="ct-body">
        <!-- Quick-stats -->
        <div class="qs-grid">
          <article
            v-for="s in quickStats"
            :key="s.label"
            class="qs-card"
            :class="'qs-' + s.variant"
          >
            <div class="qs-icon" :class="'qs-icon-' + s.variant">
              <i :class="s.iconClass"></i>
            </div>
            <div>
              <p class="qs-value">{{ s.value }}</p>
              <p class="qs-label">{{ s.label }}</p>
            </div>
          </article>
        </div>

        <!-- Two-column grid -->
        <div class="grid-2col">
          <!-- ── LEFT column ── -->
          <div class="col-stack">
            <!-- Schedule calendar -->
            <div class="bx bx-yellow">
              <div class="bx-hdr">
                <div class="bx-hdr-l">
                  <i class="fas fa-calendar-alt bx-hdr-ico"></i>
                  <span class="bx-hdr-t">Horario de Clases</span>
                </div>
                <div class="bx-hdr-r">
                  <a href="#" class="cal-vbtn">Semana</a>
                  <a href="#" class="cal-vbtn">Día</a>
                </div>
              </div>

              <div class="cal-tb">
                <div class="cal-tb-l">
                  <button class="cal-nb" @click="prevMonth" aria-label="Mes anterior">
                    <i class="fas fa-chevron-left"></i>
                  </button>
                  <button class="cal-nb" @click="nextMonth" aria-label="Mes siguiente">
                    <i class="fas fa-chevron-right"></i>
                  </button>
                  <button
                    class="cal-today"
                    @click="goToday"
                    :disabled="isCurrentMonth"
                  >Hoy</button>
                </div>
                <h2 class="cal-mtitle">{{ monthName }} {{ calYear }}</h2>
                <span class="cal-badge">Mes</span>
              </div>

              <div class="cal-wrap">
                <table class="cal-tbl">
                  <thead>
                    <tr>
                      <th v-for="d in weekdays" :key="d">{{ d }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(wk, wi) in calendarWeeks" :key="wi">
                      <td
                        v-for="(day, di) in wk"
                        :key="di"
                        :class="{
                          'cal-td-today': day.isToday,
                          'cal-td-other': !day.isCurrentMonth
                        }"
                      >
                        <span
                          class="cal-dnum"
                          :class="{
                            'cal-dnum-today': day.isToday,
                            'cal-dnum-other': !day.isCurrentMonth
                          }"
                        >{{ day.date.getDate() }}</span>
                        <div
                          v-for="ev in day.events.slice(0, 3)"
                          :key="ev.id + '-' + ev.time"
                          class="cal-ev"
                          :style="{ backgroundColor: ev.color }"
                          :title="ev.time + ' ' + ev.title"
                        >
                          <span class="cal-ev-t">{{ ev.time }}</span> {{ ev.title }}
                        </div>
                        <div v-if="day.events.length > 3" class="cal-ev-more">
                          +{{ day.events.length - 3 }} más
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Left downloads -->
            <div class="dl-col">
              <a
                href="https://sistemas.upb.edu/upload/manuales/INDUCCION_ESTUDIANTES_NUEVOS_SCZ.pdf"
                target="_blank"
                rel="noopener noreferrer"
                class="dl-btn dl-fb"
              >
                <i class="fas fa-download dl-ico"></i>
                INDUCCIÓN ESTUDIANTES NUEVOS (Descargar)
              </a>
              <a
                href="https://sistemas.upb.edu/upload/documents/Reglamento_de_Becas.pdf"
                target="_blank"
                rel="noopener noreferrer"
                class="dl-btn dl-li"
              >
                <i class="fas fa-download dl-ico"></i>
                REGLAMENTO DE BECAS (Descargar)
              </a>
            </div>
          </div>

          <!-- ── RIGHT column ── -->
          <div class="col-stack">
            <!-- News carousel -->
            <div class="bx bx-blue">
              <div class="bx-hdr">
                <span class="bx-hdr-t">Noticias UPB</span>
              </div>
              <div class="crs-wrap">
                <div
                  v-for="(img, i) in newsImages"
                  :key="i"
                  class="crs-slide"
                  :class="{ 'crs-active': i === currentSlide }"
                >
                  <img :src="img.src" :alt="img.alt" class="crs-img" />
                </div>

                <button
                  class="crs-btn crs-prev"
                  @click="prevSlide"
                  aria-label="Noticia anterior"
                >
                  <i class="fas fa-chevron-left"></i>
                </button>
                <button
                  class="crs-btn crs-next"
                  @click="nextSlide"
                  aria-label="Noticia siguiente"
                >
                  <i class="fas fa-chevron-right"></i>
                </button>

                <div class="crs-dots">
                  <button
                    v-for="(_, i) in newsImages"
                    :key="i"
                    class="crs-dot"
                    :class="{ 'crs-dot-on': i === currentSlide }"
                    @click="currentSlide = i"
                    :aria-label="'Ir a noticia ' + (i + 1)"
                  ></button>
                </div>
              </div>
            </div>

            <!-- Right downloads -->
            <div class="dl-col">
              <a
                href="https://sistemas.upb.edu/upload/documents/Reglamento_Estudiantil_Pregrado.pdf"
                target="_blank"
                rel="noopener noreferrer"
                class="dl-btn dl-am"
              >
                <i class="fas fa-download dl-ico"></i>
                REGLAMENTO ESTUDIANTIL DE PREGRADO (Descargar)
              </a>
              <a
                href="https://sistemas.upb.edu/upload/documents/REGLAMENTO_DE_EXAMENES_DE_SUFICIENCIA_2019.pdf"
                target="_blank"
                rel="noopener noreferrer"
                class="dl-btn dl-rd"
              >
                <i class="fas fa-download dl-ico"></i>
                REGLAMENTO DE EXÁMENES DE SUFICIENCIA (Descargar)
              </a>
            </div>
          </div>
        </div>

        <!-- ConectaUPB -->
        <div class="cnx-outer">
          <div class="bx bx-gold bx-nopad">
            <a
              href="https://conectaupb.net/"
              target="_blank"
              rel="noopener noreferrer"
              class="cnx-a"
            >
              <img
                src="https://sistemas.upb.edu/img/conectaUpb.png"
                alt="ConectaUPB"
                class="cnx-img"
              />
              <div class="cnx-tags">
                <span
                  v-for="tag in conectaTags"
                  :key="tag"
                  class="cnx-tag"
                >
                  <i class="fas fa-check-circle cnx-check"></i>
                  {{ tag }}
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      <div class="spacer-bottom"></div>
    </div>

    <FloatingChatWidget />
  </div>
</template>

<script>
import FloatingChatWidget from "@/components/FloatingChatWidget.vue";

export default {
  name: "StudentPortalShellView",
  components: { FloatingChatWidget },

  data() {
    return {
      sidebarOpen: true,
      userMenuOpen: false,
      openMenus: {},
      currentSlide: 0,
      carouselTimer: null,

      /* Calendar */
      calYear: new Date().getFullYear(),
      calMonth: new Date().getMonth(),
      weekdays: ["lun.", "mar.", "mié.", "jue.", "vie.", "sáb.", "dom."],
      monthNames: [
        "enero","febrero","marzo","abril","mayo","junio",
        "julio","agosto","septiembre","octubre","noviembre","diciembre",
      ],

      quickStats: [
        { label: "Inscripciones", value: "5 Materias", variant: "warning", iconClass: "fas fa-graduation-cap" },
        { label: "Asistencia",    value: "94 %",       variant: "info",    iconClass: "fas fa-chart-bar" },
        { label: "Pendientes",    value: "2 Tareas",   variant: "primary", iconClass: "fas fa-tasks" },
        { label: "Notificaciones",value: "7 Nuevas",   variant: "neutral", iconClass: "fas fa-bell" },
      ],

      navGroups: [
        {
          key: "cuenta", label: "Mi Cuenta", iconClass: "fas fa-user",
          items: [
            { label: "Perfil" },
            { label: "Cambiar Contraseña" },
            { label: "Curriculum Vitae" },
            { label: "Gestión Documentos" },
            { label: "Credencial Estudiantil" },
            { label: "Solicitar Email UPB" },
          ],
        },
        {
          key: "academico", label: "Académico", iconClass: "fas fa-graduation-cap",
          items: [
            { label: "Notas" },{ label: "Kardex" },{ label: "Malla Curricular" },
            { label: "Horario" },{ label: "Asignación de Materias" },
            { label: "Programación Académica" },{ label: "Tareas" },
            { label: "Examen Inglés" },{ label: "Cuestionarios" },
            { label: "Proceso de Graduación", badge: { text: "nuevo", cls: "sb-badge-red" } },
            { label: "Pariente(s) / Apoderado(s)" },
          ],
        },
        {
          key: "finanzas", label: "Finanzas y Trámites", iconClass: "fas fa-wallet",
          items: [
            { label: "Estado de Cuenta" },
            { label: "Trámites Estudiantiles" },
            { label: "Gestión de Beca" },
          ],
        },
        {
          key: "servicios", label: "Servicios", iconClass: "fas fa-th-large",
          items: [
            { label: "Ficha Médica" },{ label: "Registro de Vacunas" },
            { label: "Control Vehicular" },
            { label: "Trabajos & Recursos", href: "https://conectaupb.net/", external: true },
          ],
        },
        {
          key: "noticias", label: "Noticias", iconClass: "fas fa-newspaper",
          items: [
            { label: "Blog de Noticias" },
            { label: "Crear Noticia" },
          ],
        },
      ],

      newsImages: [
        { src: "https://sistemas.upb.edu/upload/elections/banner_seupb.jpg",                        alt: "Elecciones UPB" },
        { src: "https://sistemas.upb.edu/news/upload/images/bg/examendiferido.jpg",                 alt: "Examen Diferido" },
        { src: "https://sistemas.upb.edu/news/upload/images/bg/AvisoPr%C3%A1cticaEmpresarial.jpg",  alt: "Aviso Práctica Empresarial" },
        { src: "https://sistemas.upb.edu/news/upload/images/bg/Cambiodecarrera.jpg",                alt: "Cambio de Carrera" },
        { src: "https://sistemas.upb.edu/news/upload/images/bg/Estudianteoyente.jpg",               alt: "Estudiante Oyente" },
        { src: "https://sistemas.upb.edu/news/upload/images/bg/Materiaincompleta.jpg",              alt: "Materia Incompleta" },
        { src: "https://sistemas.upb.edu/news/upload/images/bg/Comunicado-UPB.jpg",                 alt: "Comunicado UPB" },
        { src: "https://sistemas.upb.edu/news/upload/images/bg/Avisogest%C3%B3ndocumentosrotativa.jpg", alt: "Aviso Gestión Documentos" },
        { src: "https://sistemas.upb.edu/news/upload/images/bg/upbvirtual.jpg",                     alt: "UPB Virtual" },
        { src: "https://sistemas.upb.edu/news/upload/images/bg/Credencial-digital-como-obtener.jpg",alt: "Credencial Digital" },
      ],

      conectaTags: [
        "Bolsa de trabajo",
        "Red de contactos",
        "Eventos",
        "Recursos & Capacitación Continua",
      ],

      sampleEvents: [
        { id: 1, title: "Álgebra",         time: "09:00", color: "#3b82f6", dow: 1 },
        { id: 2, title: "Arq. Software",   time: "11:00", color: "#8b5cf6", dow: 2 },
        { id: 3, title: "Física II",       time: "08:00", color: "#f59e0b", dow: 3 },
        { id: 4, title: "Lab. Redes",      time: "16:00", color: "#10b981", dow: 4 },
        { id: 5, title: "Cálculo III",     time: "14:00", color: "#ef4444", dow: 5 },
      ],
    };
  },

  computed: {
    currentYear() { return new Date().getFullYear(); },
    monthName()   { return this.monthNames[this.calMonth]; },
    isCurrentMonth() {
      const t = new Date();
      return this.calYear === t.getFullYear() && this.calMonth === t.getMonth();
    },
    calendarWeeks() {
      const year = this.calYear, month = this.calMonth, today = new Date();
      const first = new Date(year, month, 1);
      let startDow = first.getDay() - 1; // Mon=0
      if (startDow < 0) startDow = 6;
      const start = new Date(first);
      start.setDate(start.getDate() - startDow);

      const weeks = [];
      const cur = new Date(start);
      for (let w = 0; w < 6; w++) {
        const week = [];
        for (let d = 0; d < 7; d++) {
          const dt = new Date(cur);
          const isToday =
            dt.getFullYear() === today.getFullYear() &&
            dt.getMonth() === today.getMonth() &&
            dt.getDate() === today.getDate();
          const isCur = dt.getMonth() === month;
          const dayDow = dt.getDay() === 0 ? 7 : dt.getDay();
          const events = isCur
            ? this.sampleEvents.filter((e) => e.dow === dayDow)
            : [];
          week.push({ date: dt, isToday, isCurrentMonth: isCur, events });
          cur.setDate(cur.getDate() + 1);
        }
        weeks.push(week);
        if (cur.getMonth() !== month && w >= 4) break;
      }
      return weeks;
    },
  },

  mounted() {
    this.startCarousel();
    document.addEventListener("mousedown", this.outsideClick);
  },
  beforeUnmount() {
    this.stopCarousel();
    document.removeEventListener("mousedown", this.outsideClick);
  },

  methods: {
    toggleMenu(k) { this.openMenus = { ...this.openMenus, [k]: !this.openMenus[k] }; },
    startCarousel() {
      this.carouselTimer = setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % this.newsImages.length;
      }, 4000);
    },
    stopCarousel() { if (this.carouselTimer) clearInterval(this.carouselTimer); },
    nextSlide() { this.currentSlide = (this.currentSlide + 1) % this.newsImages.length; },
    prevSlide() { this.currentSlide = (this.currentSlide - 1 + this.newsImages.length) % this.newsImages.length; },
    prevMonth() { if (this.calMonth === 0) { this.calMonth = 11; this.calYear--; } else { this.calMonth--; } },
    nextMonth() { if (this.calMonth === 11) { this.calMonth = 0; this.calYear++; } else { this.calMonth++; } },
    goToday() { const t = new Date(); this.calYear = t.getFullYear(); this.calMonth = t.getMonth(); },
    outsideClick(e) {
      if (this.$refs.userDropdown && !this.$refs.userDropdown.contains(e.target))
        this.userMenuOpen = false;
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════════════════════════════
   Portal Shell — faithful AdminLTE-style replica
   ══════════════════════════════════════════════════════════════ */
.portal-shell {
  min-height: 100vh;
  background: #f0f4f8;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color: #1f2937;
}

/* ─── HEADER ──────────────────────────────────────────────── */
.portal-header {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 50;
  height: 64px;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #0a1628 0%, #122040 60%, #1a2e52 100%);
  box-shadow: 0 4px 14px rgba(0,0,0,.3);
}
.gold-accent-line {
  position: absolute; top: 0; left: 0; right: 0;
  height: 2px;
  background: linear-gradient(to right, #D4AF37, #f5d06e, #D4AF37);
}

.header-logo-area {
  width: 240px; flex-shrink: 0;
  display: flex; align-items: center;
  padding: 0 20px; height: 100%;
  border-right: 1px solid rgba(255,255,255,.1);
}
.logo-link { display: flex; align-items: center; }
.logo-img  { height: 32px; object-fit: contain; }

.header-nav {
  flex: 1; display: flex; align-items: center;
  justify-content: space-between; padding: 0 20px;
}
.sidebar-toggle {
  padding: 8px; border-radius: 8px; border: none;
  background: transparent; color: rgba(255,255,255,.7);
  cursor: pointer; font-size: 18px; transition: .15s;
}
.sidebar-toggle:hover { color: #D4AF37; background: rgba(255,255,255,.08); }

.header-actions { display: flex; align-items: center; gap: 4px; }

.mode-switch-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 8px;
  font-size: 12px; font-weight: 600;
  color: rgba(255,255,255,.7); text-decoration: none;
  border: 1px solid rgba(255,255,255,.15);
  background: rgba(255,255,255,.06);
  transition: .15s; margin-right: 4px;
}
.mode-switch-btn:hover {
  color: #D4AF37;
  border-color: rgba(212,175,55,.3);
  background: rgba(255,255,255,.08);
}

.hdr-icon-btn {
  position: relative; padding: 8px; border-radius: 8px;
  border: none; background: transparent;
  color: rgba(255,255,255,.6); cursor: pointer;
  font-size: 16px; transition: .15s;
}
.hdr-icon-btn:hover { color: #D4AF37; background: rgba(255,255,255,.08); }
.notif-dot {
  position: absolute; top: 6px; right: 6px;
  width: 6px; height: 6px; border-radius: 50%; background: #D4AF37;
}

/* User dropdown trigger */
.usr-dd-wrap { position: relative; margin-left: 4px; }
.usr-dd-trigger {
  display: flex; align-items: center; gap: 10px;
  padding: 6px 12px 6px 8px; border-radius: 8px;
  border: 1px solid transparent; background: transparent;
  color: #fff; cursor: pointer; transition: .15s;
}
.usr-dd-trigger:hover { background: rgba(255,255,255,.08); border-color: rgba(212,175,55,.2); }

.usr-avatar-sm {
  width: 32px; height: 32px; border-radius: 50%;
  background: #1e4d8c; color: #fff;
  display: grid; place-items: center;
  font-size: 11px; font-weight: 700;
  box-shadow: 0 0 0 2px rgba(212,175,55,.5);
}
.usr-info-sm { display: none; text-align: left; }
.usr-name-sm { margin: 0; font-size: 12px; font-weight: 600; color: #fff; line-height: 1.2; }
.usr-role-sm { margin: 0; font-size: 10px; color: #D4AF37; line-height: 1.2; }
.usr-dd-chevron { font-size: 10px; color: rgba(255,255,255,.5); transition: transform .2s; }
.usr-dd-chevron.rotated { transform: rotate(180deg); }

/* Dropdown menu */
.usr-dd-menu {
  position: absolute; right: 0; top: 100%; margin-top: 8px;
  width: 256px; border-radius: 12px; overflow: hidden;
  border: 1px solid rgba(255,255,255,.1);
  background: #0e1e36; box-shadow: 0 20px 40px rgba(0,0,0,.4); z-index: 60;
}
.dd-user-hdr {
  display: flex; align-items: center; gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #0a1628, #162545);
  border-bottom: 1px solid rgba(255,255,255,.1);
}
.usr-avatar-lg {
  width: 48px; height: 48px; border-radius: 50%;
  background: #1e4d8c; color: #fff;
  display: grid; place-items: center;
  font-size: 16px; font-weight: 700;
  box-shadow: 0 0 0 2px rgba(212,175,55,.6); flex-shrink: 0;
}
.dd-user-name { margin: 0; font-size: 14px; font-weight: 600; color: #fff; }
.dd-user-role { margin: 2px 0 0; font-size: 12px; color: #D4AF37; }
.dd-items { padding: 4px 0; }
.dd-item {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 20px; font-size: 14px;
  color: rgba(255,255,255,.7); text-decoration: none; transition: .15s;
}
.dd-item:hover { color: #fff; background: rgba(255,255,255,.05); }
.dd-item-icon { color: #D4AF37; width: 16px; text-align: center; }
.dd-footer { padding: 12px; border-top: 1px solid rgba(255,255,255,.1); }
.dd-logout {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 8px 16px; border-radius: 8px;
  font-size: 14px; color: #f87171; text-decoration: none;
  border: 1px solid rgba(248,113,113,.3); transition: .15s;
}
.dd-logout:hover { background: rgba(248,113,113,.1); }

@media (min-width: 640px) { .usr-info-sm { display: block; } }

/* ─── SIDEBAR ─────────────────────────────────────────────── */
.sb-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.5); z-index: 30;
}
.sb {
  position: fixed; top: 64px; left: 0; bottom: 0;
  width: 240px; overflow-y: auto; z-index: 40;
  display: flex; flex-direction: column;
  background: #0a1628;
  transform: translateX(-100%);
  transition: transform .2s;
}
.sb.open { transform: translateX(0); }

/* User panel */
.sb-user {
  display: flex; align-items: center; gap: 12px;
  padding: 16px; border-bottom: 1px solid rgba(255,255,255,.1);
}
.sb-avatar {
  width: 40px; height: 40px; border-radius: 50%;
  background: #1e4d8c; color: #fff;
  display: grid; place-items: center;
  font-size: 13px; font-weight: 700;
  box-shadow: 0 0 0 2px rgba(212,175,55,.5); flex-shrink: 0;
}
.sb-uname  { margin: 0; font-size: 14px; font-weight: 600; color: #fff; }
.sb-online {
  display: flex; align-items: center; gap: 4px;
  margin-top: 2px; font-size: 11px; color: #34d399;
}
.sb-online-dot {
  width: 7px; height: 7px; border-radius: 50%; background: #34d399;
}

/* Nav items */
.sb-nav { flex: 1; padding: 4px 0; }
.sb-nav ul { list-style: none; margin: 0; padding: 0; }

.sb-link {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 16px; color: #a8bec7; text-decoration: none;
  font-size: 14px; border: none; background: none;
  width: 100%; text-align: left; cursor: pointer; transition: .15s;
}
.sb-link:hover, .sb-active { background: #0d1f35; color: #D4AF37; }
.sb-icon { color: #D4AF37; width: 16px; text-align: center; font-size: 14px; }
.sb-link-grow { flex: 1; text-align: left; }
.sb-link-between { justify-content: space-between; }
.sb-link-left { display: flex; align-items: center; gap: 12px; }

.sb-chev {
  font-size: 10px; color: #7a9aaa;
  transition: transform .2s; transform: rotate(-90deg);
}
.sb-chev-open { transform: rotate(0deg); }

.sb-badge {
  font-size: 9px; padding: 2px 6px; border-radius: 4px; font-weight: 700;
}
.sb-badge-green { background: #10b981; color: #fff; }
.sb-badge-red   { background: #ef4444; color: #fff; }

.sb-sub {
  list-style: none; margin: 0; padding: 0;
  background: #071020; border-top: 1px solid rgba(255,255,255,.05);
}
.sb-sub-link {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 16px 8px 40px; font-size: 12px;
  color: #7a9aaa; text-decoration: none; transition: .15s;
}
.sb-sub-link:hover { color: #D4AF37; background: #0d1f35; }
.sb-sub-left { display: flex; align-items: center; gap: 8px; }
.sb-sub-arrow { font-size: 8px; opacity: .5; }

.sb-divider { border-top: 1px solid rgba(255,255,255,.1); margin: 4px 0; }
.sb-link-logout       { color: #f87171 !important; }
.sb-link-logout:hover { background: rgba(127,29,29,.2) !important; color: #fca5a5 !important; }

.sb-footer {
  padding: 12px 16px; border-top: 1px solid rgba(255,255,255,.1);
  font-size: 10px; color: #4a6375;
}

/* ─── MAIN AREA ───────────────────────────────────────────── */
.main-area { padding-top: 64px; transition: margin-left .2s; }

/* Content header */
.ct-header {
  padding: 12px 20px; background: #fff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
}
.ct-header-inner {
  display: flex; flex-direction: column; gap: 4px;
}
.ct-title-row { display: flex; align-items: center; gap: 8px; }
.ct-title-bar {
  width: 4px; height: 20px; border-radius: 999px;
  background: #D4AF37; flex-shrink: 0;
}
.ct-title { margin: 0; font-size: 18px; font-weight: 700; color: #0a1628; }
.ct-subtitle { margin: 2px 0 0 12px; font-size: 12px; color: #94a3b8; }
.ct-crumbs {
  list-style: none; margin: 0; padding: 0;
  display: flex; align-items: center; gap: 6px; font-size: 12px;
}
.ct-crumbs li { color: #1e4d8c; font-weight: 500; }

.ct-body { padding: 16px; }

/* ─── QUICK-STATS ─────────────────────────────────────────── */
.qs-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0,1fr));
  gap: 16px; margin-bottom: 16px;
}
.qs-card {
  background: #fff; border-radius: 4px;
  border: 1px solid #e5e7eb; border-top: 3px solid #cbd5e1;
  padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,.05);
  display: flex; align-items: center; gap: 12px;
}
.qs-warning { border-top-color: #D4AF37; }
.qs-info    { border-top-color: #1e4d8c; }
.qs-primary { border-top-color: #0d2550; }
.qs-neutral { border-top-color: #94a3b8; }

.qs-icon {
  width: 48px; height: 48px; border-radius: 8px;
  display: grid; place-items: center;
  font-size: 20px; flex-shrink: 0;
}
.qs-icon-warning { background: rgba(212,175,55,.12); color: #D4AF37; }
.qs-icon-info    { background: rgba(30,77,140,.12);  color: #1e4d8c; }
.qs-icon-primary { background: rgba(13,37,80,.12);   color: #0d2550; }
.qs-icon-neutral { background: rgba(148,163,184,.12);color: #64748b; }

.qs-value {
  margin: 0; font-size: 20px; font-weight: 800;
  color: #0f172a; line-height: 1.2;
}
.qs-label { margin: 2px 0 0; font-size: 12px; color: #64748b; }

/* ─── TWO-COLUMN GRID ─────────────────────────────────────── */
.grid-2col {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 16px; margin-bottom: 16px;
}
.col-stack { display: flex; flex-direction: column; gap: 12px; }

/* ─── BOX CARD ────────────────────────────────────────────── */
.bx {
  background: #fff; border-radius: 4px;
  border: 1px solid #e5e7eb;
  border-top: 3px solid #cbd5e1;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
}
.bx-yellow { border-top-color: #eab308; }
.bx-blue   { border-top-color: #2563eb; }
.bx-gold   { border-top-color: #D4AF37; }
.bx-nopad .bx-body { padding: 0; }

.bx-hdr {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; border-bottom: 1px solid #f1f5f9; background: #fff;
}
.bx-hdr-l { display: flex; align-items: center; gap: 8px; }
.bx-hdr-ico { font-size: 14px; color: #6b7280; }
.bx-hdr-t   { font-size: 14px; font-weight: 600; color: #374151; }
.bx-hdr-r   { display: flex; align-items: center; gap: 4px; }

.cal-vbtn {
  padding: 2px 8px; font-size: 12px;
  border: 1px solid #d1d5db; border-radius: 4px;
  color: #4b5563; text-decoration: none; transition: .15s;
}
.cal-vbtn:hover { background: #f9fafb; }

/* ─── CALENDAR ────────────────────────────────────────────── */
.cal-tb {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 12px; background: #f9fafb;
  border-bottom: 1px solid #f1f5f9;
}
.cal-tb-l { display: flex; align-items: center; gap: 4px; }
.cal-nb {
  padding: 4px; border-radius: 4px; border: none;
  background: transparent; cursor: pointer; color: #374151;
  font-size: 12px; transition: .15s;
}
.cal-nb:hover { background: #e5e7eb; }
.cal-today {
  padding: 2px 8px; font-size: 12px;
  border: 1px solid #d1d5db; border-radius: 4px;
  background: transparent; cursor: pointer; transition: .15s;
}
.cal-today:hover:not(:disabled) { background: #e5e7eb; }
.cal-today:disabled { opacity: .4; cursor: not-allowed; }
.cal-mtitle {
  margin: 0; font-size: 14px; font-weight: 600;
  color: #4b5563; text-transform: capitalize;
}
.cal-badge {
  font-size: 12px; padding: 2px 8px;
  background: #1f2937; color: #fff; border-radius: 4px;
}

.cal-wrap { overflow-x: auto; }

.cal-tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
.cal-tbl th {
  padding: 4px; text-align: center; color: #6b7280;
  font-weight: 500; font-size: 11px;
  border: 1px solid #f1f5f9; background: #f9fafb;
}
.cal-tbl td {
  border: 1px solid #f1f5f9; padding: 4px;
  vertical-align: top; width: 14.28%; background: #fff;
  min-height: 72px;
}
.cal-td-today { background: #fefce8; }
.cal-td-other { background: rgba(249,250,251,.6); }

.cal-dnum {
  display: block; text-align: right; line-height: 1;
  margin-bottom: 2px; font-size: 11px; color: #6b7280;
}
.cal-dnum-today { font-weight: 700; color: #ca8a04; }
.cal-dnum-other { color: #d1d5db; }

.cal-ev {
  color: #fff; font-size: 9px; padding: 1px 4px;
  border-radius: 2px; line-height: 1.3; margin-bottom: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cal-ev-t  { opacity: .8; }
.cal-ev-more { font-size: 9px; color: #9ca3af; padding-left: 4px; }

/* ─── NEWS CAROUSEL ───────────────────────────────────────── */
.crs-wrap {
  position: relative; background: #f3f4f6;
  aspect-ratio: 16 / 9;
}
.crs-slide {
  position: absolute; inset: 0;
  opacity: 0; transition: opacity .5s; z-index: 0;
}
.crs-active { opacity: 1; z-index: 10; }
.crs-img { width: 100%; height: 100%; object-fit: cover; display: block; }

.crs-btn {
  position: absolute; top: 50%; transform: translateY(-50%);
  z-index: 20; background: rgba(0,0,0,.4); color: #fff;
  border: none; border-radius: 4px; padding: 6px;
  cursor: pointer; font-size: 14px; transition: .15s;
}
.crs-btn:hover { background: rgba(0,0,0,.6); }
.crs-prev { left: 8px; }
.crs-next { right: 8px; }

.crs-dots {
  position: absolute; bottom: 8px; left: 50%;
  transform: translateX(-50%); z-index: 20;
  display: flex; gap: 6px;
}
.crs-dot {
  width: 8px; height: 8px; border-radius: 50%; border: none;
  background: rgba(255,255,255,.4); cursor: pointer; padding: 0;
  transition: .15s;
}
.crs-dot-on { background: #fff; }

/* ─── DOWNLOADS ───────────────────────────────────────────── */
.dl-col { display: flex; flex-direction: column; gap: 8px; }
.dl-btn {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px; border-radius: 4px;
  font-size: 14px; font-weight: 500;
  color: #fff; text-decoration: none; transition: .15s;
}
.dl-ico { font-size: 14px; flex-shrink: 0; }
.dl-fb { background: #3b5998; } .dl-fb:hover { background: #344e86; }
.dl-li { background: #007bb6; } .dl-li:hover { background: #006a9e; }
.dl-am { background: #f39c12; } .dl-am:hover { background: #e08e0b; }
.dl-rd { background: #dd4b39; } .dl-rd:hover { background: #c9402e; }

/* ─── CONECTA UPB ─────────────────────────────────────────── */
.cnx-outer { max-width: 672px; margin: 0 auto; }
.cnx-a { display: block; text-decoration: none; color: inherit; }
.cnx-img { width: 100%; display: block; }
.cnx-tags {
  padding: 8px 16px; display: flex; flex-wrap: wrap;
  justify-content: center; gap: 12px;
  font-size: 14px; color: #4b5563;
}
.cnx-tag  { display: flex; align-items: center; gap: 4px; }
.cnx-check { color: #22c55e; font-size: 13px; }

.spacer-bottom { height: 32px; }

/* ─── RESPONSIVE ──────────────────────────────────────────── */
@media (min-width: 1024px) {
  .main-area.shifted { margin-left: 240px; }
  .lg-hide { display: none !important; }
  .ct-header-inner {
    flex-direction: row; align-items: center; justify-content: space-between;
  }
}
@media (max-width: 1023px) {
  .header-logo-area { width: auto; border-right: none; padding: 0 12px; }
}
@media (max-width: 768px) {
  .qs-grid  { grid-template-columns: repeat(2,minmax(0,1fr)); }
  .grid-2col { grid-template-columns: 1fr; }
}
@media (max-width: 640px) {
  .qs-grid { grid-template-columns: 1fr; }
  .ct-header { padding: 10px 12px; }
  .ct-title  { font-size: 16px; }
  .mode-label { display: none; }
}
</style>
